import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  toggleEditMode,
  updateWidgetProperty,
  updateWidgetEditorState,
  changeFieldType
} from "actions/editor";
import { associateFontsWithWigdet } from "actions/fonts";

import _ from "lodash";
import cx from "classnames";
import { Tabs, Tab } from "react-bootstrap";

import { EditorContext } from "../../../../index";

import { Property } from "../../../../components/properties";
import { Fonts, Widgets } from "@paperflite-marketing/cleverstory-commons";
import { Font } from "../../../../components/properties/fonts";
import { Frame } from "scenejs";
import { FORM, FORM_FIELD, FORM_STEP } from "constants/widgetTypes";
import { getColorsConfig } from "services/cssHelper.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";

import styles from "./dropDownField.module.scss";

const FormDropDownWidget = function DropDownEditor({ className, style, widgetProps, widgetRef}) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");
  const dropDownState = _.get(widgetProps, "editorState.dropDownState", "closed");
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const properties = _.get(widgetProps, "properties", {});

  Fonts.useFont(_.get(widgetProps, "fonts") || []);

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, "regular"),
  };

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.dropDownField, {[styles.overlap]: dropDownState === "opened"})}
      style={widgetStyles}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    > 
      <div className={styles.container}>
      <Widgets.FormDropDownList
        className={styles.DropdownList}
        widgetProps={widgetProps}
        show={dropDownState === "opened"}
        style={{
          ...style,
          transform: `translate(0px, 0px) rotate(0deg) scale(1, 1)`,
        }}
        breakpoint={breakpoint}
      />
      </div>
    </div>
  );
};

const DropDownWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);
  };

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action onClick={handleEditMode.bind(this, "text")}>
        Edit Field
      </Property.Action>
      <Property.Divider />
      <Property.Action tooltip='Layout' onClick={() => changeView("layout")}>
        <i className={"cleverstory-distribute-horizontally"} />
      </Property.Action>
      <Property.Action tooltip='Style' onClick={() => changeView("style")}>
        <i className={"cleverstory-customize"} />
      </Property.Action>
      <Property.Action
        className={styles.iconButton}
        tooltip='Help'
        onClick={() => {
          window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, "_blank");
        }}
      >
        <i className={"cleverstory-help"} />
      </Property.Action>

      {editMode && (
        <DropdownListEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={id}
          editor={editorSession}
          breakpoint={"desktop"}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "style" && (
        <FieldDesignEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

const DropDownWidgetMobileAction = ({ editorSession, uid, ...rest }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === uid;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action tooltip='Layout' onClick={() => changeView("layout")}>
        <FontAwesomeIcon icon={faSlidersH} />
      </Property.Action>
      <Property.Divider />
      <Property.Action
        className={styles.iconButton}
        tooltip='Help'
        onClick={() => {
          window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, "_blank");
        }}
      >
        <i className={"cleverstory-help"} />
      </Property.Action>

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={uid}
          editor={editorSession}
          breakpoint={"mobile"}
          widget={rest}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

FormDropDownWidget.moveableConfig = (editorSession, widget) => {
  const widgetElement = document.querySelector(
    `#${editorSession} .editor_widget_renderer #${widget.uid}`
  );
  const formElement = widgetElement?.closest(`div[data-widget-type="${FORM}"]`);
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = formElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const fieldName = _.get(widget, `properties.desktop.field`);
  const deletable = fieldName != "email";

  const bounds = {
    left: left,
    right: left + rect?.width,
    top: top,
    bottom: top + rect?.height,
  };

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: false,
    deletable: deletable,
    renderDirections: ["e", "w"],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false;
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set("height", "auto");
      return frame.get();
    },
  };
};

FormDropDownWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === "desktop" && (
        <DropDownWidgetDesktopAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}

      {breakpoint === "mobile" && (
        <DropDownWidgetMobileAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}
    </>
  );
};

/**
 * Field input editor
 *
 */
const DropdownListEditor = (props) => {
  const dispatch = useDispatch();

  const [error, setError] = useState(null);
  const [listType, setListType] = useState("Dropdown");
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const id = _.get(props, "widget.uid");
  const field = _.get(props, "widget.properties.desktop.field");
  const label = _.get(props, "widget.properties.desktop.label") || "";
  const placeholder =
    _.get(props, "widget.properties.desktop.placeholder") || "";
  const mandatory =
    _.get(props, "widget.properties.desktop.mandatory") || false;

  const multiSelect = _.get(props, "widget.properties.desktop.multiSelect");


  const fields = useSelector((state) => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    const steps = widgets
      .filter((item) => item.type === FORM_STEP && item.parent === id)
      .sort((w1, w2) => {
        const o1 = _.get(w1, "order") || 1;
        const o2 = _.get(w2, "order") || 1;

        return o1 - o2;
      });

    const step = _.get(steps, "0.uid");
    if (!step) {
      return [];
    }

    const fields = widgets
      .filter((item) => item.type === FORM_FIELD && item.parent === step)
      .map((item) => _.get(item, "properties.desktop.field"));
    return fields;
  });

  const customFormFields = useSelector((state) =>
    _.get(state, "formFields.items", null)
  );

  const handlePropertyChange = (name, value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, "desktop", {
        [name]: value,
      })
    );
  };

  const handleChangeTarget = () => {
    if (props.widget.content) {
      let data = _.find(customFormFields, { id: props.widget.content.id });
      data = {
        ...data,
        type: "checkList_field",
      };

      const existing = _.includes(fields, data.type);
      if (!existing) {
        dispatch(
          changeFieldType(
            editorSession,
            props.widget.parent,
            data,
            props.widget
          )
        );
      }
    }
  };

  return (
    <Property.Dialog
      width={222}
      target={`#${id}`}
      className={styles.textEditorDialog}
      title="Field Settings"
      onCancel={props.onCancel}
    >
      <Property className={styles.listTypeWrapper} name="List Type">
        <div className={styles.radioConatiner}>
          <Property.RadioButton
            className={styles.radio}
            title="Dropdown"
            value={true}
          />
          <Property.RadioButton
            className={styles.radio}
            title="Open List"
            value={false}
            onClick={handleChangeTarget}
          />
        </div>
      </Property>
      <Property className={styles.selectionTypeWrapper} name="Selection Type">
        <div className={styles.radioConatiner}>
          <Property.RadioButton
            className={styles.radio}
            title="Single"
            value={!multiSelect}
            onClick={() => handlePropertyChange("multiSelect", false)}
          />
          <Property.RadioButton
            className={styles.radio}
            title="Multiple"
            value={multiSelect}
            onClick={() => handlePropertyChange("multiSelect", true)}
          />
        </div>
      </Property>
      <Property className={styles.labelWrapper} name="Label">
        <Property.Input
          value={label}
          onChange={(e) => {
            handlePropertyChange("label", e.target.value);
          }}
          placeholder="Enter field title"
          error={error}
        />
      </Property>
      <Property name="Placeholder Text">
        <Property.Input
          value={placeholder}
          onChange={(e) => {
            handlePropertyChange("placeholder", e.target.value);
          }}
          placeholder="Enter Placeholder Text"
        />
      </Property>

      {field != "email" && (
        <Property>
          <Property.Switch
            id="required"
            title={"This field is required"}
            value={mandatory}
            onChange={() => handlePropertyChange("mandatory", !mandatory)}
          />
        </Property>
      )}
    </Property.Dialog>
  );
};

/**
 * Alignment Editor
 *
 */
const AlignmentEditor = ({ id, widget, breakpoint, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const [state, setState] = useState({
    activeTab: "closed",
  });

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key,
      };
    });
  };

  useEffect(() => {
    dispatch(
      updateWidgetEditorState(editorSession, id, {
        dropDownState: state.activeTab,
      })
    );
  }, [state.activeTab]);

  useEffect(() => {
    if (state?.activeTab == 'opened') {
      rest?.onCancel();
    }
    return () => {
      dispatch( updateWidgetEditorState(editorSession, id, { dropDownState: "closed"}));
    };
  }, [id]);

  const labelPadding =
    _.get(widget, `properties.${breakpoint}.styles.layout.labelPadding`) || 0;
  const inputPadding =
    _.get(widget, `properties.${breakpoint}.styles.layout.inputPadding`) || 0;
  const fieldSpacing =
    _.get(widget, `properties.${breakpoint}.styles.layout.fieldSpacing`) || 0;
  const spaceBetweenItems =
    _.get(widget, `properties.${breakpoint}.styles.layout.spaceBetweenItems`) ||
    0;
  const horizontalPadding =
    _.get(widget, `properties.${breakpoint}.styles.layout.horizontalPadding`) ||
    0;
  const verticalPadding =
    _.get(widget, `properties.${breakpoint}.styles.layout.verticalPadding`) ||
    0;

  const handlePropertyChange = (field, value) => {
    var request = _.get(widget, `properties.${breakpoint}.styles`) || {};

    request = _.cloneDeep(request);
    _.set(request, `layout.${field}`, value);
    handleChange(request);
  };

  const handleChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  return (
    <Property.Dialog
      className={styles.fieldLayoutDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Layout"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Tabs
          activeKey={state.activeTab}
          onSelect={handleTabChange}
          className={styles.buttonStyleTabs}
        >
          <Tab
            eventKey="closed"
            title="Closed"
            className={styles.buttonStyleTab}
          >
            <div className={styles.dropdownAlignment}>
              <Property name="Label Padding" className={styles.marginProperty}>
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={labelPadding}
                  onChange={(value) =>
                    handlePropertyChange("labelPadding", value)
                  }
                />
              </Property>
              <Property name="Input Padding" className={styles.marginProperty}>
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={inputPadding}
                  onChange={(value) =>
                    handlePropertyChange("inputPadding", value)
                  }
                />
              </Property>
              <Property
                name="Space between title &amp; input"
                className={styles.marginProperty}
              >
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={fieldSpacing}
                  onChange={(value) =>
                    handlePropertyChange("fieldSpacing", value)
                  }
                />
              </Property>
            </div>
          </Tab>
          <Tab
            eventKey="opened"
            title="Opened"
            className={styles.buttonStyleTab}
          >
            <div className={styles.dropdownAlignment}>
              <Property
                name="Space Between Items"
                className={styles.marginProperty}
              >
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={spaceBetweenItems}
                  onChange={(value) =>
                    handlePropertyChange("spaceBetweenItems", value)
                  }
                />
              </Property>
              <Property
                name="Horizontal Padding"
                className={styles.marginProperty}
              >
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={horizontalPadding}
                  onChange={(value) =>
                    handlePropertyChange("horizontalPadding", value)
                  }
                />
              </Property>
              <Property
                name="Vertical Padding"
                className={styles.marginProperty}
              >
                <Property.Slider
                  min={0}
                  max={100}
                  step={1}
                  input={true}
                  value={verticalPadding}
                  onChange={(value) =>
                    handlePropertyChange("verticalPadding", value)
                  }
                />
              </Property>
            </div>
          </Tab>
        </Tabs>
      </Property.Body>
    </Property.Dialog>
  );
};

/**
 * Field design editor
 *
 */
const FieldDesignEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const [state, setState] = useState({
    activeTab: "closed",
  });

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key,
      };
    });
  };

  useEffect(() => {
    dispatch(
      updateWidgetEditorState(editorSession, id, {
        dropDownState: state.activeTab,
      })
    );
  }, [state.activeTab]);

  useEffect(() => {
    if (state?.activeTab == 'opened') {
      rest?.onCancel();
    }
    return () => {
      dispatch( updateWidgetEditorState(editorSession, id, { dropDownState: "closed"}));
    };
  }, [id]);

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const fieldStyles = state.activeTab === 'closed' ? _.get(properties, `styles.regular`, {}) : _.get(properties, `styles.openProperty`, {});
  const currentProperty = state.activeTab === 'closed' ? 'regular' : 'openProperty'
  
  const border = _.get(fieldStyles, "border", {});
  const borderColor = _.get(border, "color", null);
  const borderStyle = _.get(border, "style") || "bottom";
  const borderWidth = _.get(border, "width", 0);
  const borderRadius = _.get(border, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });

  const labelStyles = _.get(fieldStyles, "label");
  const inputStyles = _.get(fieldStyles, "input");
  const menuBackground = _.get(fieldStyles, "background");
  const menuTextColor = _.get(fieldStyles, "text");

  const handlePropertyChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  const handleBorderColorChange = (color) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.border.color`, color);
    handlePropertyChange(request);
  };

  const handleBorderStyleChange = (style) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.border.style`, style);
    handlePropertyChange(request);
  };

  const handleBorderWidthChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.border.width`, value);
    handlePropertyChange(request);
  };

  const handleBorderRadiusChange = (radius) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.border.radius`, radius);
    handlePropertyChange(request);
  };

  const handleLabelStyleChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.label`, value);
    handlePropertyChange(request);
  };

  const handleInputStyleChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `${currentProperty}.input`, value);
    handlePropertyChange(request);
  };

  const handleMenuTextColorChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `openProperty.text`, value);
    handlePropertyChange(request);
  };

  const handleMenuBackgroundChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `openProperty.background`, value);
    handlePropertyChange(request);
  };

  return (
    <Property.Dialog
      className={styles.fieldDesignDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Design"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Tabs
          activeKey={state.activeTab}
          onSelect={handleTabChange}
          className={styles.buttonStyleTabs}
        >
          <Tab
            eventKey="closed"
            title="Closed"
            className={styles.buttonStyleTab}
          >
            <div className={styles.fieldDesignProperty}>
              <Property.Accordion
                title={"Border"}
                className={styles.borderProperty}
              >
                <Property
                  name="Border Style"
                  className={styles.inputTextStyleProperty}
                >
                  <ul className={styles.borderStyles}>
                    <li
                      className={cx({
                        [styles.active]: borderStyle === "none",
                      })}
                      onClick={() => handleBorderStyleChange("none")}
                    >
                      None
                    </li>
                    <li
                      className={cx({ [styles.active]: borderStyle === "all" })}
                      onClick={() => handleBorderStyleChange("all")}
                    >
                      All Side
                    </li>
                    <li
                      className={cx({
                        [styles.active]: borderStyle === "bottom",
                      })}
                      onClick={() => handleBorderStyleChange("bottom")}
                    >
                      Bottom
                    </li>
                  </ul>
                </Property>

                {borderStyle !== "none" && (
                  <>
                    <Property.Color
                      title={"Color"}
                      clearable={false}
                      value={borderColor}
                      onChange={handleBorderColorChange}
                    />
                    <Property
                      name="Stroke Width"
                      className={styles.inputTextStyleProperty}
                    >
                      <Property.Slider
                        min={0}
                        max={10}
                        step={1}
                        input
                        value={borderWidth}
                        onChange={handleBorderWidthChange}
                      />
                    </Property>
                  </>
                )}

                <Property
                  name="Corner Radius"
                  className={styles.inputTextStyleProperty}
                >
                  <Property.BorderRadius
                    value={borderRadius}
                    onChange={handleBorderRadiusChange}
                  />
                </Property>
              </Property.Accordion>
              <FontEditor
                editorSession={editorSession}
                id={id}
                title={"Label Text"}
                type={"label"}
                value={labelStyles}
                onChange={handleLabelStyleChange}
              />
              <FontEditor
                editorSession={editorSession}
                id={id}
                title={"Input Text"}
                type={"input"}
                value={inputStyles}
                onChange={handleInputStyleChange}
              />
            </div>
          </Tab>
          <Tab
            eventKey="opened"
            title="Opened"
            className={styles.buttonStyleTab}
          >
            <div className={styles.fieldDesignProperty}>
              <Property.Accordion
                title={"Color"}
                className={styles.borderProperty}
              >
                <Property.Color
                  title="Text"
                  value={menuTextColor}
                  onChange={handleMenuTextColorChange}
                />
                <Property.Color
                  title={"Background"}
                  gradient={true}
                  clearable={true}
                  value={menuBackground}
                  onChange={handleMenuBackgroundChange}
                />
              </Property.Accordion>
              <Property.Accordion
                title={"Border"}
                className={styles.borderProperty}
              >
                <Property
                  name="Border Style"
                  className={styles.inputTextStyleProperty}
                >
                  <ul className={styles.borderStyles}>
                    <li
                      className={cx({
                        [styles.active]: borderStyle === "none",
                      })}
                      onClick={() => handleBorderStyleChange("none")}
                    >
                      None
                    </li>
                    <li
                      className={cx({ [styles.active]: borderStyle === "all" })}
                      onClick={() => handleBorderStyleChange("all")}
                    >
                      All Side
                    </li>
                    <li
                      className={cx({
                        [styles.active]: borderStyle === "bottom",
                      })}
                      onClick={() => handleBorderStyleChange("bottom")}
                    >
                      Bottom
                    </li>
                  </ul>
                </Property>

                {borderStyle !== "none" && (
                  <>
                    <Property.Color
                      title={"Color"}
                      clearable={false}
                      value={borderColor}
                      onChange={handleBorderColorChange}
                    />
                    <Property
                      name="Stroke Width"
                      className={styles.inputTextStyleProperty}
                    >
                      <Property.Slider
                        min={0}
                        max={10}
                        step={1}
                        input
                        value={borderWidth}
                        onChange={handleBorderWidthChange}
                      />
                    </Property>
                  </>
                )}

                <Property
                  name="Corner Radius"
                  className={styles.inputTextStyleProperty}
                >
                  <Property.BorderRadius
                    value={borderRadius}
                    onChange={handleBorderRadiusChange}
                  />
                </Property>
              </Property.Accordion>
            </div>
          </Tab>
        </Tabs>
      </Property.Body>
    </Property.Dialog>
  );
};

const FontEditor = ({ editorSession, id, title, type, value, onChange }) => {
  const dispatch = useDispatch();

  const background = _.get(value, "background");
  const color = _.get(value, "color");
  const placeholderColor = _.get(value, "placeholderColor");
  const fontFamily = _.get(value, "font.fontFamily");
  const fontSize = _.get(value, "font.fontSize") || 14;

  const handleBackgroundChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "background", color);
    onChange(request);
  };

  const handleColorChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "color", color);
    onChange(request);
  };

  const handlePlaceholderColorChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "placeholderColor", color);
    onChange(request);
  };

  const handleFontFamilyChange = (font) => {
    var fontId = _.get(font, "font.id");
    var familyValue = _.get(font, "font.fontFamily");
    var request = _.cloneDeep(value);
    _.set(request, "font.fontFamily", familyValue);
    onChange(request);

    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [fontId]));
    }
  };

  const handleFontSizeChange = (size) => {
    var request = _.cloneDeep(value);
    _.set(request, "font.fontSize", size);
    onChange(request);
  };

  return (
    <>
      <Property.Accordion title={title} className={styles.textProperty}>
        {type === "input" && (
          <Property.Color
            title={"Background"}
            gradient={true}
            clearable={true}
            value={background}
            onChange={handleBackgroundChange}
          />
        )}

        <Property.Color
          title={type === "input" ? "Input Color" : "Label Color"}
          value={color}
          onChange={handleColorChange}
        />

        {type === "input" && (
          <Property.Color
            title={"Placeholder Color"}
            value={placeholderColor}
            onChange={handlePlaceholderColorChange}
          />
        )}
        <Font.Family
          value={fontFamily}
          onChange={handleFontFamilyChange}
          className={styles.inputTextStyleProperty}
        />
        <Font.Size
          value={fontSize}
          onChange={handleFontSizeChange}
          className={styles.inputTextStyleProperty}
        />
      </Property.Accordion>
    </>
  );
};

/**
 * --------------------------------------------------
 * Field Styles
 * --------------------------------------------------
 */
function getFieldStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const activeStyles = _.get(defaultProperties, `${active}`, {});

  //alignment
  const alignment =
    _.get(deviceProperties, "styles.layout.alignment") || "left";
  styles["alignItems"] = alignment;

  if (alignment === "left") {
    styles["alignItems"] = "flex-start";
  } else if (alignment === "right") {
    styles["alignItems"] = "flex-end";
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = "inherit";
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color,
  };

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = _.get(border, "radius.topLeft", null);
  const topRight = _.get(border, "radius.topRight", null);
  const bottomLeft = _.get(border, "radius.bottomLeft", null);
  const bottomRight = _.get(border, "radius.bottomRight", null);

  if (topLeft) {
    styles["borderTopLeftRadius"] = parseInt(topLeft) + "px";
  }

  if (topRight) {
    styles["borderTopRightRadius"] = parseInt(topRight) + "px";
  }

  if (bottomLeft) {
    styles["borderBottomLeftRadius"] = parseInt(bottomLeft) + "px";
  }

  if (bottomRight) {
    styles["borderBottomRightRadius"] = parseInt(bottomRight) + "px";
  }

  const borderStyle = _.get(border, "style") || "bottom";
  if (borderStyle !== "none") {
    const borderWidthString = _.get(border, "width") || 0;
    var borderWidget = parseInt(borderWidthString) + "px";

    const fieldName =
      borderStyle == "all" ? "borderWidth" : "borderBottomWidth";
    styles[fieldName] = borderWidget;

    const colorRgb = _.get(border, "color");
    var color = "transparent";
    if (colorRgb) {
      color = getColorsConfig(colorRgb);
    }

    styles["borderColor"] = color;
  }

  return styles;
}

export function getFontStyle(fontStyles) {
  var styles = {};

  if (!fontStyles) {
    return styles;
  }

  //background-color
  const backgroundColorRgb = _.get(fontStyles, "background", null);
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb),
    };
  }

  //font-color
  const colorRgb = _.get(fontStyles, "color", null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color,
  };

  //font-family
  const fontFamily = _.get(fontStyles, "font.fontFamily", "inherit");
  styles = {
    ...styles,
    fontFamily: fontFamily,
  };

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14;
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + "px";
    styles["fontSize"] = fontSize;
  }

  return styles;
}

FormDropDownWidget.processRequest = (widget) => {
  if(_.get(widget, 'content.id')){
    widget = {
      ...widget,
      content: _.get(widget, 'content.id')
    }
  }
  return widget;
};

export { FormDropDownWidget };
